<template>
  <div v-if="showBottomNav" :class="['dock', isIosDevice ? 'ios-dock' : '']">
    <div class="dock-scroll">
      <!-- Loop for all non-Home items -->
      <div
        v-for="(option, index) in options"
        :key="index"
        :class="['dock-item', localSelected === index ? 'active' : '', isIosDevice ? 'ios-dock-item' : '']"
        @click="setSelected(index, option.path)"
        @mouseover="hoverEffect($event, true)"
        @mouseleave="hoverEffect($event, false)"
      >
        <v-icon :size="isIosDevice ? 28 : 26">{{ option.icon }}</v-icon>
        <span class="dock-title">{{ $t(option.titleKey) }}</span>
      </div>

      <!-- Separate Home Button -->
      <div class="dock-item home-item" @click="goToHome" v-if="customer && customer.toLowerCase() === 'pia'">
        <v-icon :size="isIosDevice ? 28 : 26">mdi-home-outline</v-icon>
        <span class="dock-title">{{ $t('navigation-bar.home') }}</span>
      </div>
    </div>

    <!-- Floating Components Stack (positioned from bottom to top) -->
    <ProfileCreationDialog ref="profileDialog" />

    <!-- Agent Assistant (bottom-most floating component) -->
    <AgentAssistant v-if="showAgentAssistant" />

    <!-- Position Dialog (middle floating component) -->
    <PositionDialog v-if="showPositionDialog === false" />

    <!-- Position Tracker Button (top-most floating component, above all others) -->
    <PositionTrackerIosButton v-if="isIosDevice && !showPositionTracker && isLocationPluginAvailable" />
    <PositionTrackerAndroidButton v-if="isAndroidDevice && !showPositionTracker && isLocationPluginAvailable" />
  </div>
</template>

<script>
  import { ref, computed, onMounted, watch, nextTick } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { VIcon } from 'vuetify/lib/components/VIcon'
  import ProfileCreationDialog from '@/components/general/ChildComponents/ProfileCreationDialog.vue'
  import { getStoredItem } from '@/services/utils'
  import AgentAssistant from '@/ai/aiComponents/AgentAssistant.vue'
  import PositionDialog from '@/components/general/Positioning/PositionDialog.vue'
  import PositionTrackerIosButton from '@/components/general/Positioning/PositionTrackerIosButton.vue'
  import PositionTrackerAndroidButton from '@/components/general/Positioning/PositionTrackerAndroidButton.vue'
  import { isIos, useOAuthService } from '@/services/OAuthService'
  import { getCustomer } from '@/assets/Branding/branding.js'
  import { getFeatures, getUserProfiles } from '@/controllers/BaseController'
  import { Device } from '@capacitor/device'
  import { Capacitor } from '@capacitor/core'

  // Constants
  const NAVIGATION_OPTIONS = [
    { 
      id: 1, 
      icon: 'mdi-newspaper-variant-outline', 
      title: 'Info', 
      titleKey: 'navigation-bar.info',
      path: { name: 'InfoCenterView' } 
    },
    { 
      id: 2, 
      icon: 'mdi-office-building-marker-outline', 
      title: 'Space', 
      titleKey: 'navigation-bar.space',
      path: { name: 'BuildingView' } 
    },
    { 
      id: 3, 
      icon: 'mdi-calendar-month-outline', 
      title: 'Booking', 
      titleKey: 'navigation-bar.booking',
      path: { name: 'BookingPoisList' } 
    },
    { 
      id: 4, 
      icon: 'mdi-key-outline', 
      title: 'Locks', 
      titleKey: 'navigation-bar.locks',
      path: { name: 'LocksView' } 
    },
    { 
      id: 5, 
      icon: 'mdi-chat-outline', 
      title: 'Chat', 
      titleKey: 'navigation-bar.chat',
      path: { name: 'ChatUsersList' } 
    },
    { 
      id: 6, 
      icon: 'mdi-menu-open', 
      title: 'Menu', 
      titleKey: 'navigation-bar.menu',
      path: { name: 'MenuView' } 
    }
  ]

  const EXCLUDED_ROUTES = ['Login']
  const EXCLUDED_URL_PATTERNS = ['/oauth2', '/home']
  const isLocationPluginAvailable = ref(false)
  
  export default {
    name: 'BottomNavigation',
    components: {
      VIcon,
      ProfileCreationDialog,
      AgentAssistant,
      PositionDialog,
      PositionTrackerIosButton,
      PositionTrackerAndroidButton
    },

    setup() {
      // Core routing
      const route = useRoute()
      const router = useRouter()
      const { t } = useI18n()
      const { isLoggedIn, getUserProfile } = useOAuthService()

      // Platform detection state
      const isIosDevice = ref(false)
      const isAndroidDevice = ref(false)

      // State management
      const state = {
        customer: ref(undefined),
        localSelected: ref(0),
        options: ref([...NAVIGATION_OPTIONS])
      }

      // Component visibility flags
      const componentVisibility = {
        showAgentAssistant: ref(false),
        showPositionDialog: ref(true), // Always show
        showPositionTracker: ref(true) // Always show
      }

      // Device detection function
      async function detectDevice() {
        try {
          const info = await Device.getInfo()
          isIosDevice.value = info.platform === 'ios'
          isAndroidDevice.value = info.platform === 'android'

          console.log('BottomNavigation: Device platform detected:', {
            platform: info.platform,
            isIos: isIosDevice.value,
            isAndroid: isAndroidDevice.value
          })
        } catch (error) {
          console.warn('BottomNavigation: Failed to get device info:', error)
          // Fallback: assume Android for non-iOS platforms
          isIosDevice.value = false
          isAndroidDevice.value = true
        }
      }

      // Refs
      const profileDialog = ref(null)

      // Computed properties
      const showBottomNav = computed(() =>
        route.name &&
        !EXCLUDED_ROUTES.includes(route.name) &&
        !EXCLUDED_URL_PATTERNS.some(pattern => window.location.href.includes(pattern))
      )

      // Utility functions
      const platformUtils = {
        async detectPlatform() {
          try {
            const { Capacitor } = await import('@capacitor/core')
            return typeof Capacitor.isNativePlatform === 'function'
              ? Capacitor.isNativePlatform()
              : Capacitor.isNativePlatform === true
          } catch (error) {
            console.warn('BottomNavigation: Platform detection failed, defaulting to web', error)
            return false
          }
        }
      }

      const featureUtils = {
        filterOptionsByFeatures(options, features, isNative, customer) {
          return options.filter(option => {
            // Filter Locks for web platform (except PIA)
            if (option.title === 'Locks' && !isNative && customer?.toLowerCase() !== 'pia') {
              console.log('BottomNavigation: Filtering out Locks for web platform')
              return false
            }

            // Filter by feature flags
            if (option.title === 'Chat' && !features?.chat?.enabled) {
              console.log('BottomNavigation: Chat disabled by feature flag')
              return false
            }

            if (option.title === 'Booking' && !features?.booking?.enabled) {
              console.log('BottomNavigation: Booking disabled by feature flag')
              return false
            }

            return true
          })
        },

        setComponentVisibility(features) {
          componentVisibility.showAgentAssistant.value = features?.ai?.enabled ?? true
          // Position components always visible (handle disabled state internally)
          componentVisibility.showPositionDialog.value = true
          componentVisibility.showPositionTracker.value = true
        }
      }

      const navigationUtils = {
        setSelected(index, path) {
          state.localSelected.value = index
          router.push(path)
        },

        hoverEffect(event, isHover) {
          const item = event.currentTarget
          if (item) {
            item.style.transform = isHover ? 'scale(1.1)' : 'scale(1)'
          }
        },

        goToHome() {
          router.push({ name: 'HomeView', path: '/home' })
        },

        updateSelectedFromRoute(newPath) {
          const routeMap = {
            '/infocenter': 'Info',
            '/building': 'Space',
            '/booking': 'Booking',
            '/locks': 'Locks',
            '/chat': 'Chat',
            '/menu': 'Menu'
          }

          for (const [path, title] of Object.entries(routeMap)) {
            if (newPath.includes(path)) {
              const index = state.options.value.findIndex(opt => opt.title === title)
              if (index !== -1) {
                state.localSelected.value = index
              }
              break
            }
          }
        }
      }

      const profileUtils = {
        async checkAndShowProfileDialog() {
          try {
            const isProfileCompleted = await getStoredItem('ProfileCompleted')
            if (isProfileCompleted) return

            if (!profileUtils.shouldShowProfileDialog()) return

            const isAuthenticated = await isLoggedIn()
            if (!isAuthenticated) return

            const userProfiles = await getUserProfiles()
            if (!userProfiles?.length) return

            const { email } = await getUserProfile()
            const userProfile = userProfiles.find(profile => profile.email === email)

            if (!userProfile) {
              await nextTick()
              setTimeout(() => {
                if (profileDialog.value?.openDialog) {
                  profileDialog.value.openDialog()
                }
              }, 1500)
            }
          } catch (error) {
            console.error('BottomNavigation: Profile check failed:', error)
          }
        },

        shouldShowProfileDialog() {
          return route.name &&
                 route.name !== 'Login' &&
                 !EXCLUDED_URL_PATTERNS.some(pattern => window.location.href.includes(pattern))
        }
      }

      const blueIdUtils = {
        async stopScanningOnRouteChange(oldPath, newPath) {
          const isLeavingLocks = oldPath?.includes('/locks') && !newPath.includes('/locks')
          if (!isLeavingLocks) return

          try {
            const [{ Capacitor }, { BlueIDAccess }] = await Promise.all([
              import('@capacitor/core'),
              import('@blueid/access-capacitor')
            ])

            const isNative = typeof Capacitor.isNativePlatform === 'function'
              ? Capacitor.isNativePlatform()
              : Capacitor.isNativePlatform === true

            if (isNative && window?.BlueIDAccess?.runCommand) {
              const isActive = await BlueIDAccess.runCommand('isScanningActive')
              if (isActive) {
                console.log('BottomNavigation: Auto-stopping BlueID scan (left LocksView)')
                await BlueIDAccess.runCommand('stopScanning')
              }
            }
          } catch (error) {
            console.warn('BottomNavigation: Failed to stop BlueID scan:', error)
          }
        }
      }

      // Initialization
      const initializeComponent = async () => {
        console.log('BottomNavigation: Initializing component')
        console.log('Registered Plugins:', Capacitor.Plugins);

        try {
          // Detect device platform first
          await detectDevice()

          // Parallel execution for better performance
          const [iosResult, customerResult, featuresResult, isNative] = await Promise.all([
            isIos(),
            getCustomer(),
            getFeatures(),
            platformUtils.detectPlatform()
          ])

          // Update state
          state.customer.value = customerResult

          // Filter navigation options
          state.options.value = featureUtils.filterOptionsByFeatures(
            NAVIGATION_OPTIONS,
            featuresResult,
            isNative,
            customerResult
          )

          if (Capacitor.Plugins?.BackgroundGeolocation) {
            console.log('✅ BackgroundGeolocation plugin is registered and available');
            isLocationPluginAvailable.value = true;
          } else {
            console.warn('❌ BackgroundGeolocation plugin is NOT registered');
            isLocationPluginAvailable.value = false;
          }

          // Set component visibility
          featureUtils.setComponentVisibility(featuresResult)

          if(route.name === 'Chat' || route.path?.includes('/request-demo')) {
            componentVisibility.showAgentAssistant.value = false
          }
   
          console.log('BottomNavigation: Initialization completed')
        } catch (error) {
          console.error('BottomNavigation: Initialization failed:', error)

          // Fallback state
          componentVisibility.showAgentAssistant.value = true
          componentVisibility.showPositionDialog.value = true
          componentVisibility.showPositionTracker.value = true
        }
      }

      // Lifecycle hooks
      onMounted(initializeComponent)

      // Route watcher with optimized handlers
      watch(
        () => route.path,
        async (newPath, oldPath) => {
          if (!newPath) return

          try {
            // Handle route-specific logic
            await Promise.all([
              profileUtils.checkAndShowProfileDialog(),
              blueIdUtils.stopScanningOnRouteChange(oldPath, newPath)
            ])

            // Update navigation selection
            if (oldPath?.includes('/booking') && newPath.includes('/building')) {
              // Skip update when going from booking to building
              return
            }
            navigationUtils.updateSelectedFromRoute(newPath)

          } catch (error) {
            console.error('BottomNavigation: Route change handler failed:', error)
          }
        },
        { immediate: true }
      )

      // Return public interface
      return {
        // State
        ...state,
        showBottomNav,
        profileDialog,
        isIosDevice,
        isAndroidDevice,
        isLocationPluginAvailable,

        // Component visibility
        ...componentVisibility,

        // Methods
        setSelected: navigationUtils.setSelected,
        hoverEffect: navigationUtils.hoverEffect,
        goToHome: navigationUtils.goToHome,
        
        // i18n
        t
      }
    }
  }
</script>
<style scoped>
  /* Main dock container */
  .dock {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 0;
    background: linear-gradient(180deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(255, 255, 255, 0.98) 100%);
    backdrop-filter: blur(20px);
    box-shadow:
      0 -8px 32px rgba(0, 0, 0, 0.08),
      0 -2px 8px rgba(0, 0, 0, 0.04);
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
  }

  .ios-dock {
    padding: 8px 0 calc(8px + env(safe-area-inset-bottom));
  }

  .dock-scroll {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    padding: 0 8px;
    gap: 2px;
  }

  /* Individual dock items */
  .dock-item {
    flex: 1 1 auto;
    min-width: 44px;
    max-width: 68px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 8px 4px;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }

  .ios-dock-item {
    padding: 10px 6px;
    transform: translateY(-2px);
  }

  .dock-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(67, 167, 249, 0.1), rgba(67, 167, 249, 0.05));
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .dock-item:hover::before {
    opacity: 1;
  }

  .dock-item:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow:
      0 8px 25px rgba(67, 167, 249, 0.15),
      0 3px 10px rgba(0, 0, 0, 0.08);
  }

  .dock-item:active {
    transform: translateY(0px) scale(0.98);
    transition: all 0.1s ease;
  }

  /* Active state styling */
  .active {
    background: linear-gradient(135deg,
      rgba(67, 167, 249, 0.15) 0%,
      rgba(67, 167, 249, 0.08) 100%);
    box-shadow:
      0 4px 20px rgba(67, 167, 249, 0.2),
      0 2px 8px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px) scale(1.02);
    border-radius: 12px;
    max-width: 64px;
  }

  .active::before {
    opacity: 0;
  }

  /* Icon styling */
  .v-icon {
    font-size: 24px;
    color: #4a5568;
    transition: all 0.3s ease;
    z-index: 1;
    position: relative;
  }

  .dock-item:hover .v-icon {
    color: #43a7f9;
    transform: scale(1.1);
  }

  .dock-title {
    margin-top: 4px;
    font-size: 10px;
    font-weight: 500;
    color: #64748b;
    text-align: center;
    line-height: 1.2;
    transition: all 0.3s ease;
    z-index: 1;
    position: relative;
  }

  .dock-item:hover .dock-title {
    color: #43a7f9;
    font-weight: 600;
  }

  .dock-item.active .v-icon,
  .dock-item.active .dock-title {
    color: #43a7f9;
  }

  .dock-item.active .v-icon {
    transform: scale(1.08);
  }

  /* HOME BUTTON STYLING */
  .home-item {
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
    color: white;
    box-shadow:
      0 4px 20px rgba(33, 150, 243, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
    padding: 8px 6px;
    margin-left: 8px;
    margin-right: 6px;
    transform: none !important;
    flex: 0 0 56px;
    max-width: 60px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }

  .home-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .home-item:hover::before {
    opacity: 1;
  }

  .home-item:hover {
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
    transform: translateY(-2px) scale(1.05) !important;
    box-shadow:
      0 8px 30px rgba(33, 150, 243, 0.4),
      0 4px 15px rgba(0, 0, 0, 0.15);
  }

  .home-item .v-icon {
    font-size: 26px !important;
    color: white !important;
    transform: none;
  }

  .home-item:hover .v-icon {
    color: white !important;
    transform: scale(1.1);
  }

  .home-item .dock-title {
    font-size: 10px;
    font-weight: 600;
    color: white !important;
    margin-top: 3px;
  }

  .home-item:hover .dock-title {
    color: white !important;
  }

  /* RESPONSIVE DESIGN */
  @media (max-width: 768px) {
    .dock {
      padding: 4px 0;
    }

    .dock-item {
      padding: 6px 3px;
      max-width: 64px;
      min-width: 42px;
    }

    .dock-title {
      font-size: 9px;
    }

    .v-icon {
      font-size: 22px;
    }

    .active {
      border-radius: 10px;
      padding: 5px 3px;
      max-width: 60px;
    }

    .home-item {
      flex: 0 0 52px;
      max-width: 56px;
      padding: 6px 4px;
      border-radius: 14px;
    }

    .home-item .v-icon {
      font-size: 24px !important;
    }

    .home-item .dock-title {
      font-size: 9px;
    }
  }

  @media (max-width: 480px) {
    .dock {
      padding: 3px 0;
    }

    .dock-scroll {
      padding: 0 6px;
      gap: 1px;
    }

    .dock-item {
      padding: 5px 2px;
      max-width: 60px;
      min-width: 40px;
      border-radius: 10px;
    }

    .dock-title {
      font-size: 8px;
    }

    .v-icon {
      font-size: 20px;
    }

    .active {
      border-radius: 8px;
      max-width: 56px;
    }

    .home-item {
      flex: 0 0 48px;
      max-width: 52px;
      padding: 5px 3px;
      border-radius: 12px;
    }

    .home-item .v-icon {
      font-size: 22px !important;
    }

    .home-item .dock-title {
      font-size: 8px;
    }
  }

  /* ANIMATIONS */
  @keyframes subtle-pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.02);
      opacity: 0.9;
    }
  }

  .active {
    animation: subtle-pulse 3s ease-in-out infinite;
  }

  @keyframes subtle-glow {
    0% {
      box-shadow: 0 0 6px rgba(33, 150, 243, 0.2);
    }

    50% {
      box-shadow: 0 0 12px rgba(33, 150, 243, 0.4);
    }

    100% {
      box-shadow: 0 0 6px rgba(33, 150, 243, 0.2);
    }
  }

  /* SAFE AREA SUPPORT */
  @supports (padding: max(0px)) {
    .ios-dock {
      padding-bottom: max(8px, env(safe-area-inset-bottom));
    }
  }

  /* DARK MODE SUPPORT */
  @media (prefers-color-scheme: dark) {
    .dock {
      background: linear-gradient(180deg,
        rgba(30, 30, 30, 0.95) 0%,
        rgba(20, 20, 20, 0.98) 100%);
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .dock-item::before {
      background: linear-gradient(135deg, rgba(67, 167, 249, 0.15), rgba(67, 167, 249, 0.08));
    }

    .v-icon {
      color: #e2e8f0;
    }

    .dock-title {
      color: #cbd5e0;
    }

    .dock-item:hover .v-icon,
    .dock-item:hover .dock-title {
      color: #60a5fa;
    }

    .dock-item.active .v-icon,
    .dock-item.active .dock-title {
      color: #60a5fa;
    }
  }
</style>
